import { reportPlanSummaryMap } from "@ishvaram/domain-data/report-plan-summaries";
import type { ComponentType, ReactNode } from "react";

import OfferImpression, { type OfferImpressionFields } from "@/components/OfferImpression";
import DepthGate from "@/components/OfferPlacement.gate";
import type { PaidReportCTAProps, ReportPlanId } from "@/components/PaidReportCTA.types";
import { offerPresentationId } from "@/lib/analytics-offer-milestones";

/**
 * An offer the current page cannot check out yet: it routes the seeker to the
 * birth-details form, where the kundali result's offer opens the real checkout.
 * Server-safe on purpose (no "use client"): calculator and timing pages keep
 * the offer in their SSR HTML, and only the impression, the depth gate and the
 * CTA are client islands. The checkout chunk is never imported here.
 *
 * The click is the navigation event `report_input_collection_started`, never
 * the buying milestone. The form link carries `origin_placement` so the checkout
 * that follows can credit the placement that sent the seeker.
 *
 * The look comes from `presentation`: the boxed report card
 * (OfferPlacement.collect-inputs-card) or the inline next-step section
 * (OfferPlacement.page-bridge). Each page imports only the one it renders, so a
 * timing page never ships the card's click stack and a calculator never ships
 * the section's.
 */
export interface CollectInputsPlacementProps extends OfferImpressionFields {
  mode: "collect_inputs";
  presentation: ComponentType<CollectInputsPresentationProps>;
  placement: string;
  sourcePath: string;
  planId: ReportPlanId;
  intent: string;
  /** The birth-details form link (see buildReportInputHref). */
  href: string;
  /** Defaults to the plan's catalog price. */
  priceInr?: number;
  headline: string;
  subhead: string;
  language?: "en" | "hi";
  /** Show the masked paid-depth preview above the CTA. */
  gate?: boolean;
  ctaLabel?: string;
  note?: string;
  reportNoun?: string;
  headingLevel?: "h2" | "h3";
  /** The wrapper's classes. */
  className?: string;
  /** The card's wrapper element. */
  wrapper?: "div" | "section";
  /** A parent that already emits this offer's impression. */
  impressionMode?: "default" | "external";
  /** Overrides the click attribution built from the impression fields. */
  analyticsContext?: PaidReportCTAProps["analyticsContext"];
}

export type CollectInputsPreset = Omit<CollectInputsPlacementProps, "headline" | "subhead">;

/** What a presentation receives: the resolved link, price and slots. */
export interface CollectInputsPresentationProps extends CollectInputsPlacementProps {
  language: "en" | "hi";
  priceInr: number;
  presentationId: string;
  impression: ReactNode;
  gated: (cta: ReactNode) => ReactNode;
}

export function CollectInputsPlacement(props: CollectInputsPlacementProps) {
  const { placement, sourcePath, planId, intent, language = "en" } = props;
  const Presentation = props.presentation;
  const presentationId = offerPresentationId({
    actionMode: "input_collection",
    planId,
    placement,
    sourcePath,
  });
  const impression =
    props.impressionMode === "external" ? null : (
      <OfferImpression
        {...impressionFields(props)}
        planId={planId}
        actionMode="input_collection"
        sourcePath={sourcePath}
        intent={intent}
        placement={placement}
        presentationKey={props.presentationKey}
      />
    );
  return (
    <Presentation
      {...props}
      href={withOriginPlacement(props.href, placement)}
      language={language}
      priceInr={priceFor(props)}
      presentationId={presentationId}
      impression={impression}
      gated={(cta) =>
        props.gate ? (
          <DepthGate planId={planId} language={language} tapFollowsCta>
            {cta}
          </DepthGate>
        ) : (
          cta
        )
      }
    />
  );
}

function priceFor(props: CollectInputsPlacementProps): number {
  if (props.priceInr !== undefined) return props.priceInr;
  return reportPlanSummaryMap[props.planId as keyof typeof reportPlanSummaryMap].price_inr;
}

/** Hash params ride to the form client-side, so no server log ever sees them. */
function withOriginPlacement(href: string, placement: string): string {
  const param = `origin_placement=${encodeURIComponent(placement)}`;
  return href.includes("#") ? `${href}&${param}` : `${href}#${param}`;
}

function impressionFields(props: CollectInputsPlacementProps): OfferImpressionFields {
  return {
    checkoutChannel: props.checkoutChannel,
    commerceSystem: props.commerceSystem,
    confidence: props.confidence,
    customerProblem: props.customerProblem,
    journeyAction: props.journeyAction,
    landingIntent: props.landingIntent,
    landingPath: props.landingPath,
    offerType: props.offerType,
    rankedOfferOrder: props.rankedOfferOrder,
    routeRankReason: props.routeRankReason,
    routeRankScore: props.routeRankScore,
    urgency: props.urgency,
  };
}
